import React, { useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { useToast } from "../../hooks/useToast";
import { IntegrationConfigurationForm } from "../../forms/IntegrationConfigurationForm";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { VStack, HStack } from "@chakra-ui/layout";
import { Skeleton, SkeletonText } from "@chakra-ui/react";
import { ConfigurationTypeNames } from "../../enums/ConfigurationTypeNames";
import { IntegrationEMDISConfigurationForm } from "../../forms/IntegrationEMDISConfigurationForm";
import { IntegrationEbetClientConfigurationForm } from "../../forms/IntegrationEbetClientConfigurationForm";
// Temporarily unused: IntegrationIGTAClientConfigurationForm invents an empty
// CreateMember object on save when MembershipManagementSettings exists but
// CreateMember does not. Re-enable with that form once the submit path is fixed.
// import { IntegrationIGTAClientConfigurationForm } from "../../forms/IntegrationIGTAClientConfigurationForm";

export const IntegrationConfiguration = () => {
  const { businessId, integrationId, configurationTypeName } = useParams();
  const navigate = useNavigate();
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();

  const { data: integration, isLoading } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !integrationId) {
        return null;
      }
      return apiClient.getBusinessIntegration(businessId, integrationId);
    },
    [businessId, integrationId]
  );

  const onSuccess = useCallback(() => {
    showSuccessToast("Integration configuration updated.");
    navigate(
      `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`,
      { replace: true }
    );
  }, [showSuccessToast, businessId, integration]);

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Integration configuration update failed.");
    },
    [showErrorToast]
  );

  const configurationFormView = (configurationTypeName: string | undefined) => {
    switch (configurationTypeName) {
      case ConfigurationTypeNames.EMDISConfiguration:
        return (
          <IntegrationEMDISConfigurationForm
            businessId={businessId!}
            integrationId={integration?.id!}
            configuration={integration?.Configuration}
            apiClient={apiClient}
            onCancel={() => {
              navigate(
                `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`
              );
            }}
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
        );
      case ConfigurationTypeNames.EbetClientConfiguration:
        return (
          <IntegrationEbetClientConfigurationForm
            businessId={businessId!}
            integrationId={integration?.id!}
            configuration={integration?.Configuration}
            apiClient={apiClient}
            onCancel={() => {
              navigate(
                `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`
              );
            }}
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
        );
      // case ConfigurationTypeNames.IGTAClientConfiguration:
      //   return (
      //     <IntegrationIGTAClientConfigurationForm
      //       businessId={businessId!}
      //       integrationId={integration?.id!}
      //       configuration={integration?.Configuration}
      //       apiClient={apiClient}
      //       onCancel={() => {
      //         navigate(
      //           `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`
      //         );
      //       }}
      //       onSuccess={onSuccess}
      //       onFailure={onFailure}
      //     />
      //   );
      default:
        return (
          <IntegrationConfigurationForm
            businessId={businessId!}
            integrationId={integration?.id!}
            configuration={integration?.Configuration}
            apiClient={apiClient}
            onCancel={() => {
              navigate(
                `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`
              );
            }}
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
        );
    }
  };

  return (
    <>
      <PageHeading>
        <BusinessBreadcrumbs>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations`}
            >
              Integrations
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations/${integration?.id}`}
            >
              {integration?.IntegrationName}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations/${integration?.id}/configuration`}
            >
              Configuration
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BusinessBreadcrumbs>
        <PageHeading.Title>Integration Configuration</PageHeading.Title>
      </PageHeading>
      <PageContent>
        {(isLoading && (
          <VStack
            w="100%"
            p={6}
            boxShadow="lg"
            alignItems="flex-start"
            spacing={4}
          >
            <Skeleton w="100px" height="40px" />
            <SkeletonText w="100%" mt="4" noOfLines={20} spacing="4" />
            <HStack w="100%">
              <Skeleton w="80px" height="40px" />
              <Skeleton w="80px" height="40px" />
              <Skeleton w="80px" height="40px" />
            </HStack>
          </VStack>
        )) ||
          configurationFormView(configurationTypeName)}
      </PageContent>
    </>
  );
};
